import { ErrorStack } from "@/types/errors/ErrorStack";
import { ProgressIndicatorType } from "@/types/ProgressIndicator";
import { ServerEvents } from "@/types/ServerEvents";
import { AnalysesClient, EtlJobStatus, FileResponse, Job, JobStatus, ProgressUpdate } from "@api/apiClient";
import axios from "@api/axiosInstance";
import Connector from "@api/signalrConnection";
import useAppStore from "@hooks/useAppStore";
import i18n from "@i18n/i18n";
import { useEffect, useState } from "react";
import { useDebouncedCallback } from "use-lodash-debounce";

// Simple inline Service Worker cache invalidation
const invalidateServiceWorkerCache = (modelId: string) => {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: 'INVALIDATE_PROJECT_MODEL_CACHE',
      modelId: modelId
    });
  }
};

// Centralized cache clearing for model operations
const clearModelCache = (modelId: string, queryClient: any) => {
  // Remove all cached data for this model
  queryClient.removeQueries({
    predicate: (query: any) => {
      const queryKey = query.queryKey;
      return (queryKey[0] === "projectModel" && queryKey[1] === modelId) ||
        (queryKey[0] === "categories" && queryKey[1] === modelId) ||
        (queryKey[0] === "elements" && queryKey[1] === modelId) ||
        (queryKey[0] === "components" && queryKey[1] === modelId) ||
        (queryKey[0] === "materials" && queryKey[1] === modelId);
    }
  });
};

// TODO: As a developer, I would like a cache manager that manages cache invalidation using typed keys
// TODO: As a developer, I would like a document the summarizes all of the cached data and when things are invalidated
// TODO: As a developer, I would like a mechanism to subscribe to server events in any component so that I can respond to events locally
// TODO: As a user, I would improved error reporting from background tasks
// TODO: As a user, I would like improved error reporting in the UI with full details of the error
// TODO: As a developer, I would like the production logging configuration to be adjusted for better error tracking
// TODO: As a developer, I would like the server events to be typed and documented from the server side so I know what parameters to expect
// TODO: Design Automation server events onJobComplete and onJobFailure are clearing the entire queryClient cache -- this is too aggressive
const useServerEvents = () => {
  const appStore = useAppStore();
  const { eventHandlers } = Connector();
  const [notifyMessage, setNotifyMessage] = useState<string>("");
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [openErrors, setOpenErrors] = useState(0);

  const debounceInvalidateEtlJobs = useDebouncedCallback(async () => {
    await appStore.queryClient.invalidateQueries({ queryKey: ["etlJobs"] });
  }, 1000);

  useEffect(() => {
    if (openErrors > 0) {
      appStore.errorManager.setOpenErrors(true);
    }
  }, [openErrors]);

  useEffect(() => {
    const serverEvents: ServerEvents = {
      // Use the Job Status cache in AppStore so we track progress 
      // for indicators added to the Projects, Project and Model pages
      onJobComplete: async (_jobId: string, job: Job) => {
        console.log("signalr onJobComplete: ", job);
        if (job.status?.toLocaleLowerCase() === "success") {
          appStore.jobStatus.completeJob(job);
          // TODO: we need to clear only relevant cache, not everything
          appStore.queryClient.clear();
          setNotifyMessage(i18n.t("app.job-complete-event.message", { name: job.model?.apsItemName }));
          setNotifyOpen(true);
        }
        // TODO: this may interfere with the export excel progress indicator
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.DesignAutomation, appStore.jobStatus.hasActiveJobs());
      },

      onJobFailure: async (_jobId: string, job: Job) => {
        console.log("signalr onJobFailure: ", job);

        if (job.status?.toLocaleLowerCase() === "failed") {
          appStore.jobStatus.failedJob(job);
          // TODO: we need to clear only relevant cache, not everything
          appStore.queryClient.clear();
          setNotifyMessage(i18n.t("app.job-failure-event.message", { name: job.model?.apsItemName }));
          setNotifyOpen(true);
        }

        // TODO: this may interfere with the export excel progress indicator
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.DesignAutomation, false);
      },

      onJobStatus: async (_jobId: string, jobStatus: JobStatus) => {
        console.log("signalr onJobStatus: ", jobStatus);

        appStore.jobStatus.upsertStatus(jobStatus);

        // Show progress indicator in header (no toast notification to avoid spam)
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.DesignAutomation, appStore.jobStatus.hasActiveJobs());
      },

      onJobProgress: async (_jobId: string, progressUpdate: ProgressUpdate) => {
        console.log("signalr onJobProgress: ", progressUpdate);
        
        // Store progress in the job status store (displayed in header panel and grid)
        appStore.jobStatus.upsertProgress(progressUpdate);
        
        // Keep progress indicator visible during both Extract and Import phases
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.DesignAutomation, appStore.jobStatus.hasActiveJobs());
      },
      onModelImportJobStatus: async (_jobId: string, fileName: string, jobStatus: EtlJobStatus) => {
        console.log("signalr onModelImportJobStatus: ", fileName);

        setNotifyMessage(
          i18n.t("app.job-status-event.message", {
            name: fileName,
            status: i18n.t("app.progress-message.in-progress"),
          }),
        );
        setNotifyOpen(true);
        appStore.etlJobStatus.upsert(jobStatus);

        // TODO: this may interfere with the export excel progress indicator
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.ExcelImport, true);
      },
      onModelImportJobComplete: async (_jobId: string, fileName: string, jobStatus: EtlJobStatus) => {
        console.log("signalr onModelImportJobComplete: ", fileName);
        setNotifyMessage(
          i18n.t("app.job-status-event.message", {
            name: fileName,
            status: i18n.t("app.progress-message.complete"),
          }),
        );
        setNotifyOpen(true);
        appStore.etlJobStatus.completeJob(jobStatus);
        //appStore.etlJobStatus.completeJob(jobStatus);

        debounceInvalidateEtlJobs();

        // TODO: this may interfere with the export excel progress indicator
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.ExcelImport, false);
      },
      onExcelExportStart: async (_projectId: string, _jobId: string) => {
        setNotifyMessage(i18n.t("app.excel-export-event.start.message"));
        setNotifyOpen(true);
      },
      onExcelExportComplete: async (projectId: string, jobId: string) => {
        try {
          const analysesClient = new AnalysesClient(undefined, axios);
          const fileContent: FileResponse = await analysesClient.getExportSelectedData(projectId, jobId);
          if (fileContent) {
            const contentType = fileContent.headers!["content-type"];
            const filename = fileContent.headers!["content-disposition"].split("filename=")[1].split(";")[0].replace(/"/g, "");
            const url = window.URL.createObjectURL(new Blob([fileContent.data], { type: contentType }));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
          }
        } finally {
          appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.ExcelExport, false);
        }
      },
      onExcelExportFailure: async (_projectId: string, _jobId: string) => {
        ErrorStack.create({
          title: i18n.t("app.excel-export-event.failure.title"),
          message: i18n.t("app.excel-export-event.failure.message"),
        });
        setOpenErrors((val) => val + 1);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.ExcelExport, false);
      },
      onMultiProjectExportStart: async (_jobId: string) => {
        setNotifyMessage(i18n.t("app.multi-project-export-event.start.message"));
        setNotifyOpen(true);
      },
      onMultiProjectExportComplete: async (jobId: string, partialSuccessMessage?: string) => {
        try {
          const analysesClient = new AnalysesClient(undefined, axios);
          const fileContent: FileResponse = await analysesClient.getExportMultiProjectData(jobId);
          if (fileContent) {
            const contentType = fileContent.headers!["content-type"];
            const filename = fileContent.fileName || `multi-project-export-${new Date().toISOString().split("T")[0]}.zip`;
            const url = window.URL.createObjectURL(new Blob([fileContent.data], { type: contentType }));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          }

          // Show notification message for partial success
          if (partialSuccessMessage) {
            setNotifyMessage(partialSuccessMessage);
          } else {
            setNotifyMessage(i18n.t("app.multi-project-export-event.complete.message"));
          }
          setNotifyOpen(true);
        } finally {
          appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.MultiProjectExport, false);
        }
      },
      onMultiProjectExportFailure: async (_jobId: string, errorMessage?: string) => {
        ErrorStack.create({
          title: i18n.t("app.multi-project-export-event.failure.title"),
          message: errorMessage || i18n.t("app.multi-project-export-event.failure.message"),
        });
        setOpenErrors((val) => val + 1);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.MultiProjectExport, false);
      },
      onCloneScenarioStatus: async (scenarioName: string) => {
        console.log(scenarioName);
        // Set the notification message
        const notifyMessage = `${i18n.t("app.clone-scenario-event.title", {
          name: scenarioName,
          status: i18n.t("app.progress-message.in-progress"),
        })}`;
        setNotifyMessage(notifyMessage);
        setNotifyOpen(true);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.CloneScenario, true);
      },
      onCloneScenarioComplete: async (scenarioName: string) => {
        console.log(scenarioName);
        const notifyMessage = `${i18n.t("app.clone-scenario-event.title", {
          name: scenarioName,
          status: i18n.t("app.progress-message.complete"),
        })}`;
        setNotifyMessage(notifyMessage);
        setNotifyOpen(true);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.CloneScenario, false);
        appStore.setCloneScenarioId(scenarioName);

      },

      onCloneScenarioFailure: async (scenarioName: string) => {
        console.log(scenarioName);
        ErrorStack.create({
          title: i18n.t("app.clone-scenario-event.failure.title"),
          message: i18n.t("app.clone-scenario-event.failure.message"),
        });
        setOpenErrors((val) => val + 1);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.CloneScenario, false);
      },

      onCopyModelMaterialStatus: async (modelName: string) => {
        setNotifyMessage(
          i18n.t("app.copy-model-material-event.title", {
            name: modelName,
            status: i18n.t("app.progress-message.in-progress"),
          }),
        );
        setNotifyOpen(true);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.CopyModelMaterials, true);
      },
      onCopyModelMaterialComplete: async (modelName: string) => {
        setNotifyMessage(
          i18n.t("app.copy-model-material-event.title", {
            name: modelName,
            status: i18n.t("app.progress-message.complete"),
          }),
        );
        setNotifyOpen(true);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.CopyModelMaterials, false);
        appStore.setCopyModelMaterial(modelName);

      },

      onCopyModelMaterialFailure: async (modelName: string) => {
        ErrorStack.create({
          title: i18n.t("app.copy-model-material-event.failure.title"),
          message: modelName,
        });
        setOpenErrors((val) => val + 1);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.CopyModelMaterials, false);
      },

      onApsUsersSyncStatus: async () => {
        setNotifyMessage(
          i18n.t("app.aps-users-sync-event.title", {
            status: i18n.t("app.aps-users-sync-event.status.message"),
          }),
        );
        setNotifyOpen(true);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.SyncApsUsers, true);
      },
      onApsUsersSyncComplete: async () => {
        setNotifyMessage(
          i18n.t("app.aps-users-sync-event.title", {
            status: i18n.t("app.aps-users-sync-event.complete.message"),
          }),
        );
        setNotifyOpen(true);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.SyncApsUsers, false);
      },

      onApsUsersSyncFailure: async () => {
        ErrorStack.create({
          title: i18n.t("app.aps-users-sync-event.title"),
          message: i18n.t("app.aps-users-sync-event.failure.message"),
        });
        setOpenErrors((val) => val + 1);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.SyncApsUsers, false);
      },

      onDeleteProjectComplete: async (_projectId: string, projectName: string) => {
        setNotifyMessage(
          i18n.t("app.delete-project-event.title", {
            status: i18n.t("app.delete-project-event.complete.message", { projectName }),
          }),
        );
        setNotifyOpen(true);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.SyncApsUsers, false);
      },
      onDeleteProjectFailure: async (_projectId: string, projectName: string) => {
        ErrorStack.create({
          title: i18n.t("app.delete-project-event.title"),
          message: i18n.t("app.delete-project-event.failure.message", { projectName }),
        });
        setOpenErrors((val) => val + 1);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.DeleteProject, false);
      },
      onModelSplitStart: async (_ProjectId: string, _ModelId: string) => {
        setNotifyMessage(
          i18n.t("app.model-split-event.title", {
            status: i18n.t("app.model-split-event.start.message"),
          }),
        );

        setNotifyOpen(true);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.SplitModel, false);
      },
      onModelSplitComplete: async (_ProjectId: string, _modelId: string) => {
        setNotifyMessage(
          i18n.t("app.model-split-event.title", {
            status: i18n.t("app.model-split-event.complete.message"),
          }),
        );

        setNotifyOpen(true);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.SplitModel, false);
      },
      onModelSplitFailure: async (_ProjectId: string, _modelId: string) => {
        ErrorStack.create({
          title: i18n.t("app.model-split-event.title"),
          message: i18n.t("app.model-split-event.failure.message"),
        });
        setOpenErrors((val) => val + 1);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.SplitModel, false);
      },

      onCreateVersionStatus: async (message: string) => {
        console.log("Version creation status:", message);
        setNotifyMessage(i18n.t("app.create-version-event.status.message", { message }));
        setNotifyOpen(true);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.CreateVersion, true);
      },

      onCreateVersionComplete: async (response: any) => {
        setNotifyMessage(i18n.t("app.create-version-event.complete.message", {
          versionName: response.versionName || "New Version"
        }));
        setNotifyOpen(true);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.CreateVersion, false);

        const currentProjectId = appStore.project?.projectId;
        const newModelId = response.modelId;

        // Invalidate Service Worker cache for the new model
        if (newModelId) {
          invalidateServiceWorkerCache(newModelId);
        }

        // Invalidate project data to get updated model list
        if (currentProjectId) {
          await appStore.queryClient.invalidateQueries({ queryKey: ["project", currentProjectId] });
          await appStore.queryClient.refetchQueries({ queryKey: ["project", currentProjectId] });
        }

        // Invalidate all model-related queries
        await appStore.queryClient.invalidateQueries({ queryKey: ["projectModel"] });
        await appStore.queryClient.invalidateQueries({ queryKey: ["categories"] });
        await appStore.queryClient.invalidateQueries({ queryKey: ["elements"] });
        await appStore.queryClient.invalidateQueries({ queryKey: ["components"] });
        await appStore.queryClient.invalidateQueries({ queryKey: ["materials"] });
        await appStore.queryClient.invalidateQueries({ queryKey: ["scenarios"] });
        await appStore.queryClient.invalidateQueries({ queryKey: ["co2MaterialAssignSettings"] });

        // Set new model as current after a brief delay
        if (newModelId && currentProjectId) {
          setTimeout(() => {
            const updatedProject = appStore.project;
            const newModel = updatedProject?.models?.find(m => m.modelId === newModelId);
            if (newModel) {
              appStore.setModel(newModel);
            }
          }, 500);
        }
      },

      onCreateVersionFailure: async (message: string) => {
        ErrorStack.create({
          title: i18n.t("app.create-version-event.failure.title"),
          message: i18n.t("app.create-version-event.failure.message", {
            error: message || "Failed to create model version. Please try again."
          }),
        });
        setOpenErrors((val) => val + 1);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.CreateVersion, false);
      },

      onDeleteModelStatus: async (message: string) => {
        setNotifyMessage(i18n.t("app.delete-model-event.status.message", { message }));
        setNotifyOpen(true);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.DeleteModel, true);
      },

      onDeleteModelComplete: async (response: any) => {
        setNotifyMessage(i18n.t("app.delete-model-event.complete.message", {
          modelName: response.modelName || response.ModelName || "Model"
        }));
        setNotifyOpen(true);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.DeleteModel, false);

        // Get current project ID to invalidate the correct query
        const currentProjectId = appStore.project?.projectId;
        // Try multiple possible property names for model ID (same as update version)
        const deletedModelId = response.ModelId || response.modelId || response.Id || response.id || appStore.model?.modelId;

        // Step 1: Invalidate Service Worker cache for deleted model
        if (deletedModelId) {
          invalidateServiceWorkerCache(deletedModelId);
        }

        // If the deleted model was the current model, reset selection first
        if (appStore.model?.modelId === deletedModelId) {
          appStore.setModel(null);
        }

        // Specifically invalidate and refetch project data with the correct projectId
        if (currentProjectId) {
          await appStore.queryClient.invalidateQueries({ queryKey: ["project", currentProjectId] });

          // Refetch project data to get updated model list
          await appStore.queryClient.refetchQueries({ queryKey: ["project", currentProjectId] });
        }

        // Remove all cached data for the deleted model
        clearModelCache(deletedModelId, appStore.queryClient);

        // Invalidate general queries to refresh any components that don't use specific model IDs
        await appStore.queryClient.invalidateQueries({ queryKey: ["projectModel"] });
        await appStore.queryClient.invalidateQueries({ queryKey: ["scenarios"] });
        await appStore.queryClient.invalidateQueries({ queryKey: ["co2MaterialAssignSettings"] });

        // After project data is refreshed, set default model if needed
        setTimeout(() => {
          if (!appStore.model && currentProjectId) {
            appStore.setDefaultModel();
          }
        }, 100); // Small delay to ensure project data is loaded
      },

      onDeleteModelFailure: async (message: string) => {
        ErrorStack.create({
          title: i18n.t("app.delete-model-event.failure.title"),
          message: i18n.t("app.delete-model-event.failure.message", {
            error: message || "Failed to delete model. Please try again."
          }),
        });
        setOpenErrors((val) => val + 1);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.DeleteModel, false);
      },

      onUpdateVersionStatus: async (message: string) => {
        setNotifyMessage(`Update: ${message}`);
        setNotifyOpen(true);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.UpdateVersion, true);
      },

      onUpdateVersionComplete: async (response: any) => {
        setNotifyMessage(`Model "${response.ModelName || "Model"}" updated successfully`);
        setNotifyOpen(true);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.UpdateVersion, false);

        const currentModelId = response.ModelId || response.modelId || response.Id || response.id || appStore.model?.modelId;
        const currentProjectId = appStore.project?.projectId;

        if (currentModelId) {
          // Invalidate Service Worker cache
          invalidateServiceWorkerCache(currentModelId);

          // Remove all cached data for this model
          clearModelCache(currentModelId, appStore.queryClient);

          // Invalidate all model-related queries (triggers refetch automatically)
          await appStore.queryClient.invalidateQueries({ queryKey: ["projectModel"] });
          await appStore.queryClient.invalidateQueries({ queryKey: ["categories"] });
          await appStore.queryClient.invalidateQueries({ queryKey: ["elements"] });
          await appStore.queryClient.invalidateQueries({ queryKey: ["components"] });
          await appStore.queryClient.invalidateQueries({ queryKey: ["materials"] });
        }

        // Invalidate project data to refresh model list if needed
        if (currentProjectId) {
          await appStore.queryClient.invalidateQueries({ queryKey: ["project", currentProjectId] });
        }
      },

      onUpdateVersionFailure: async (response: any) => {
        ErrorStack.create({
          title: "Model Update Failed", // ← FIX: Direct text instead of missing i18n
          message: response.message || response || "Failed to update model version. Please try again.",
        });
        setOpenErrors((val) => val + 1);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.UpdateVersion, false);
      },

      onSustainabilitySyncStart: async () => {
        const message = i18n.t("app.sustainability-sync-event.in-progress.message");
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.SustainabilitySync, true);
        appStore.setSustainabilitySyncInProgress(message);
      },
      onSustainabilitySyncComplete: async () => {
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.SustainabilitySync, false);
        appStore.setSustainabilitySyncInProgress(null);
        setNotifyMessage(i18n.t("app.sustainability-sync-event.complete.message"));
        setNotifyOpen(true);
        await appStore.queryClient.invalidateQueries({ queryKey: ["co2MaterialTypes"] });
        await appStore.queryClient.invalidateQueries({ queryKey: ["materials"] });
      },
      onSustainabilitySyncFailure: async (errorMessage?: string) => {
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.SustainabilitySync, false);
        appStore.setSustainabilitySyncInProgress(null);
        ErrorStack.create({
          title: i18n.t("app.sustainability-sync-event.failure.title"),
          message: errorMessage || i18n.t("app.sustainability-sync-event.failure.message"),
        });
        setNotifyMessage(i18n.t("app.sustainability-sync-event.failure.message"));
        setNotifyOpen(true);
        setOpenErrors((val) => val + 1);
      },
    };

    eventHandlers(serverEvents);
  }, []);

  return { notifyMessage, notifyOpen, setNotifyOpen };
};

export default useServerEvents;